import { Link } from 'react-router-dom';
import { Skeleton } from '@ah/design';
import { BookOpen, Check, Clock } from 'lucide-react';
import type { BookListItem } from '../shared/index.ts';

export function olid(olKey: string): string {
  return olKey.replace(/^\/works\//, '');
}

export function BookRow({
  book,
  progress,
}: {
  book: BookListItem;
  progress?: { read: number; total: number };
}) {
  const pct = progress && progress.total ? Math.round((progress.read / progress.total) * 100) : 0;
  const isFinished = !!(progress && progress.total > 0 && progress.read >= progress.total);
  const isStarted = !!(progress && progress.read > 0 && !isFinished);

  return (
    <Link
      to={`/library/books/${olid(book.olKey)}`}
      className="flex gap-3 p-3 bg-card rounded-xl border border-border active:bg-accent"
    >
      <div className="relative w-16 aspect-[2/3] rounded-md overflow-hidden bg-muted shrink-0">
        {book.coverUrl ? (
          <img
            src={book.coverUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground">
            <BookOpen size={20} className="opacity-40" />
          </div>
        )}
        {isFinished ? (
          <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full w-5 h-5 grid place-items-center shadow">
            <Check size={11} />
          </div>
        ) : null}
        {isStarted ? (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
        ) : null}
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <h2 className="text-sm font-semibold leading-snug line-clamp-2">{book.title}</h2>
        <p className="text-xs text-muted-foreground line-clamp-1">
          {book.authors.map((a) => a.name).join(', ') || '—'}
        </p>
        {book.summary?.themes?.length ? (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {book.summary.themes.slice(0, 2).map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wide rounded-full border px-1.5 py-0.5 text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
        <div className="mt-auto">
          {isStarted ? (
            <span className="text-[11px] text-primary font-medium">
              {pct}% · {progress!.read} of {progress!.total} chapters
            </span>
          ) : isFinished ? (
            <span className="text-[11px] text-primary font-medium inline-flex items-center gap-1">
              <Check size={10} /> Finished
            </span>
          ) : book.summary?.readingMinutesEst ? (
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
              <Clock size={10} /> {book.summary.readingMinutesEst} min read
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

export function BookListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex gap-3 p-3 bg-card rounded-xl border border-border">
          <Skeleton className="w-16 aspect-[2/3] rounded-md shrink-0" />
          <div className="flex-1 space-y-2 py-1">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ErrorRetry({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="text-center py-16">
      <p className="text-sm text-destructive mb-3">{label}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent"
      >
        Retry
      </button>
    </div>
  );
}
